import React, { useState } from 'react';
import axios from 'axios';
import StarRating from '../StarRating';
import { fetchReviews, updateReview, deleteReview } from '../../services/organisationService';
import { calculateAverageRating } from '../LawyerProfile/utils/reviewUtils';
import { useAuth } from '../../contexts/AuthContext';
import ReviewModal from '../ReviewModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

const SORT_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  HIGHEST: 'highest',
  LOWEST: 'lowest'
};

const SOURCE_OPTIONS = {
  ALL: 'all',
  ZAHOOT: 'zahoot',
  GOOGLE: 'google'
};

const ReviewsTab = ({ organisation, reviews, onReviewAdded }) => {
  const { user } = useAuth();
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: '',
    name: '',
    email: ''
  });
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.NEWEST);
  const [sourceFilter, setSourceFilter] = useState(SOURCE_OPTIONS.ALL);
  const [locationFilter, setLocationFilter] = useState('all');
  // Edit/delete state
  const [reviewToEdit, setReviewToEdit] = useState(null);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Ensure reviews data is handled correctly
  const reviewsArray = reviews && reviews.reviews ? reviews.reviews : Array.isArray(reviews) ? reviews : [];
  const reviewStats = reviews && reviews.stats ? reviews.stats : null;
  
  console.log('ReviewsTab: organisation', organisation);
  console.log('ReviewsTab: raw reviews', reviews);
  console.log('ReviewsTab: processed reviewsArray', reviewsArray);
  console.log('ReviewsTab: reviewStats', reviewStats);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisation.id}/reviews`, 
        newReview
      );
      setNewReview({ rating: 5, comment: '', name: '', email: '' });
      await fetchReviews(organisation.id, true);
      if (onReviewAdded) {
        await onReviewAdded();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  // Edit review functionality
  const handleEditClick = (review) => {
    setReviewToEdit(review);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (updatedReview) => {
    try {
      setIsProcessing(true);
      await updateReview(reviewToEdit.id, updatedReview);
      
      // Close modal and refresh reviews
      setShowEditModal(false);
      setReviewToEdit(null);
      
      // Refresh reviews
      await fetchReviews(organisation.id, true);
      if (onReviewAdded) {
        await onReviewAdded();
      }
      
      toast.success('Review updated successfully!');
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error(error.response?.data?.message || 'Failed to update review. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete review functionality
  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsProcessing(true);
      await deleteReview(reviewToDelete.id);
      
      // Close modal and refresh reviews
      setShowDeleteModal(false);
      setReviewToDelete(null);
      
      // Refresh reviews
      await fetchReviews(organisation.id, true);
      if (onReviewAdded) {
        await onReviewAdded();
      }
      
      toast.success('Review deleted successfully!');
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error(error.response?.data?.message || 'Failed to delete review. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format date with ordinal suffix and time
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    
    // Format the date part (day with ordinal suffix)
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Format the time part (with AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} @ ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  
  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Get source-specific icon
  const getSourceIcon = (source) => {
    if (source === 'google') {
      return (
        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  // Check if a review belongs to the current user
  const isUserReview = (review) => {
    if (!user) return false;
    return review.source === 'zahoot' && user.email === review.email;
  };

  // Check if the current user can delete a review
  const canDeleteReview = (review) => {
    if (!user) return false;
    
    // User can delete their own review
    const isOwnReview = review.source === 'zahoot' && user.email === review.email;
    
    // Admin and manager can delete any review
    const isAdminOrManager = user.role === 'admin' || user.role === 'manager' || 
                            user.role_name === 'admin' || user.role_name === 'manager';
    
    return isOwnReview || isAdminOrManager;
  };

  const filteredAndSortedReviews = () => {
    let filtered = reviews;

    // Source filter
    if (sourceFilter === SOURCE_OPTIONS.ZAHOOT) {
      filtered = filtered.filter(review => review.source === 'zahoot');
    } else if (sourceFilter === SOURCE_OPTIONS.GOOGLE) {
      filtered = filtered.filter(review => review.source === 'google');
    }

    // Location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(review => {
        if (locationFilter === 'no-location') {
          return !review.location_name || review.location_name === '';
        }
        return review.location_name === locationFilter;
      });
    }

    // Sort
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case SORT_OPTIONS.NEWEST:
          return new Date(b.time || b.created_at || b.review_time) - new Date(a.time || a.created_at || a.review_time);
        case SORT_OPTIONS.OLDEST:
          return new Date(a.time || a.created_at || a.review_time) - new Date(b.time || b.created_at || b.review_time);
        case SORT_OPTIONS.HIGHEST:
          return (b.rating || 0) - (a.rating || 0);
        case SORT_OPTIONS.LOWEST:
          return (a.rating || 0) - (b.rating || 0);
        default:
          return 0;
      }
    });

    return sorted;
  };

  // Get unique locations for filter dropdown
  const getUniqueLocations = () => {
    const googleReviews = reviews.filter(review => review.source === 'google');
    const locations = [...new Set(googleReviews.map(review => review.location_name).filter(Boolean))];
    return locations.sort();
  };

  return (
    <div className="reviews-tab">
      {/* Reviews Column */}
      <div className="reviews-tab__reviews-column">
        {/* Filters */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-4">
            <select
              className="form-select rounded-md border-gray-300"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value={SOURCE_OPTIONS.ALL}>All reviews</option>
              <option value={SOURCE_OPTIONS.ZAHOOT}>Zahoot reviews</option>
              <option value={SOURCE_OPTIONS.GOOGLE}>Google reviews</option>
            </select>
            
            {/* Location filter - only show if there are Google reviews with locations */}
            {getUniqueLocations().length > 0 && (
              <select
                className="form-select rounded-md border-gray-300"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="all">All locations</option>
                {getUniqueLocations().map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
                <option value="no-location">No location specified</option>
              </select>
            )}
            
            <select
              className="form-select rounded-md border-gray-300"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value={SORT_OPTIONS.NEWEST}>Newest first</option>
              <option value={SORT_OPTIONS.OLDEST}>Oldest first</option>
              <option value={SORT_OPTIONS.HIGHEST}>Highest rated</option>
              <option value={SORT_OPTIONS.LOWEST}>Lowest rated</option>
            </select>
          </div>
        </div>

        {filteredAndSortedReviews().length > 0 ? (
          filteredAndSortedReviews().map((review) => (
            <div key={`${review.source}-${review.id}`} className="reviews-tab__review">
              {/* Left Column - Avatar */}
              <div className="reviews-tab__review-author-avatar">
                {(review.name || review.author_name || 'A')[0].toUpperCase()}
              </div>

              {/* Right Column - Review Content */}
              <div className="flex-1">
                <div className="reviews-tab__review-author-info">
                  <div className="reviews-tab__review-rating">
                    <StarRating rating={review.rating || 0} />
                  </div>
                  <div className="reviews-tab__review-author-name">
                    <span className="reviews-tab__review-author">
                      {review.name || review.author_name || 'Anonymous'}
                    </span>
                    <div className="flex items-center space-x-1">
                      {getSourceIcon(review.source)}
                      <span className="text-sm text-gray-500">
                        {review.source === 'google' ? 'Google Review' : 'Verified Review'}
                      </span>
                      {/* Location badge for Google reviews */}
                      {review.source === 'google' && review.location_name && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                          📍 {review.location_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="reviews-tab__review-date">
                    {review.source === 'google' 
                      ? (review.relative_time_description || formatDate(review.time || review.created_at))
                      : formatDate(review.time || review.created_at || review.review_time)}
                  </span>
                </div>
                <div className="reviews-tab__review-content">
                  <p>{review.comment || review.text || 'No comment provided'}</p>
                </div>
                
                {/* Edit/Delete buttons for user's own reviews */}
                {isUserReview(review) && (
                  <div className="mt-2 flex space-x-2 justify-end">
                    <button 
                      onClick={() => handleEditClick(review)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(review)}
                      className="text-sm text-red-600 hover:text-red-800 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
                
                {/* Delete button for admin/manager for reviews that aren't their own */}
                {!isUserReview(review) && canDeleteReview(review) && (
                  <div className="mt-2 flex space-x-2 justify-end">
                    <button 
                      onClick={() => handleDeleteClick(review)}
                      className="text-sm text-red-600 hover:text-red-800 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="reviews-tab__empty-state">
            <div className="reviews-tab__empty-state-icon">📝</div>
            <p className="reviews-tab__empty-state-text">No reviews yet</p>
            <p className="reviews-tab__empty-state-subtext">Be the first to review this organisation</p>
          </div>
        )}
      </div>

      {/* Form Column */}
      <div className="reviews-tab__form-column">
        <div className="reviews-tab__form-sticky">
          <div className="reviews-tab__form">
            <h3 className="reviews-tab__form-title">Leave a Review</h3>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Rating</label>
                <div className="mt-1">
                  <StarRating 
                    rating={newReview.rating} 
                    onChange={(rating) => setNewReview({ ...newReview, rating })}
                    editable 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={newReview.name}
                  onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={newReview.email}
                  onChange={(e) => setNewReview({ ...newReview, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Review</label>
                <textarea
                  required
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                />
              </div>
              <button
                type="submit"
                className="btn btn--primary"
              >
                Submit Review
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Review Modal */}
      <ReviewModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setReviewToEdit(null);
        }}
        onSubmit={handleEditSubmit}
        lawyerName={organisation?.practice_name || 'this organisation'}
        isEdit={true}
        initialReview={reviewToEdit}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReviewToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemType="Review"
      />
    </div>
  );
};

export default ReviewsTab; 